import { Appointment } from "@/features/appointments/types/Appointment";
import { useNutritionistConsult } from "@/features/experts/hooks/useNutritionistConsult";
import { useSpecialistConsult } from "@/features/experts/hooks/useSpecialistConsult";
import { useHealthAssessment } from "@/features/health-assessment/hooks/useHealthAssessment";
import { useBloodTracker } from "@/features/holifya-tests/blood-test/hooks/useBloodTracker";
import { useGetDocuments } from "@/features/ocr/hooks/useGetDocuments";
import { BloodTestTrackingStatus } from "@/utils/enums/TrackingStatus";
import { useConsultsStore } from "@/utils/stores/useConsultsStore";
import { useNavigate } from "react-router-dom";

export const useSubHealthWidget = () => {
    const { bloodTracker, updateTracker } = useBloodTracker()
    const navigate = useNavigate()
    const { isHealthAssessmentCompleted } = useHealthAssessment()
    const healthAssessmentCompleted = isHealthAssessmentCompleted()
    const { data: documents } = useGetDocuments();
    const filesUploaded = documents !== undefined && documents.length !== 0
    const { shouldPushBooking } = useSpecialistConsult()
    const { shouldPushNutritionistBooking, nutriConsultsLeft} = useNutritionistConsult()
    const { consultsData } = useConsultsStore()
    const specialistConsultsLeft = consultsData.specialistConsultsLeft
    const nutritionistConsultsLeft = consultsData.nutritionistConsultsLeft

    type BloodWidgetConfig = {
        text: string;
        buttonText: string;
        onClick: () => void;
        expertRole?: string,
        consult?: Appointment,
        variant?: "default" | "success";
    };

    const configMap: Partial<Record<BloodTestTrackingStatus, BloodWidgetConfig>> = {
        [BloodTestTrackingStatus.Purchased]: {
            text: "📦 Hai ricevuto il kit?",
            buttonText: "Sì, ricevuto",
            onClick: () => updateTracker(BloodTestTrackingStatus.Delivered),
        },
        [BloodTestTrackingStatus.Delivered]: {
            text: "🩸 Effettua l'analisi degli Aminoacidi",
            buttonText: "Effettua",
            onClick: () => navigate("/blood-test/tutorial"),
        },
        [BloodTestTrackingStatus.TestDone]: {
            text: "🔐 Registra il Codice campione",
            buttonText: "Registra",
            onClick: () => navigate("/blood-test/sample-register"),
        },
        [BloodTestTrackingStatus.SampleRegistered]: {
            text: "🚛 Richiedi il ritiro del campione",
            buttonText: "Richiedi",
            onClick: () => navigate("/blood-test/packing"),
        },
        [BloodTestTrackingStatus.PickupRequested]: {
            text: "🚛 Modifica il ritiro del campione",
            buttonText: "Modifica",
            onClick: () => navigate("/blood-test/request-pickup"),
        },
    };

    const defaultReturn = !healthAssessmentCompleted ? {
        text: "📝 Rispondi all'Health Assessment",
        buttonText: "Rispondi",
        onClick: () => { navigate("/health-assessment-home-page") },
        variant: "default",
    } : !filesUploaded ? {
        text: "📄 Carica i tuoi referti",
        buttonText: "Carica",
        onClick: () => { navigate("/reports") },
        variant: "default",
    } : shouldPushBooking && specialistConsultsLeft && specialistConsultsLeft > 0 ? {
        text: "👩‍⚕️️ Prenota consulto con medico",
        buttonText: "Prenota",
        onClick: () => { navigate("/equipe/your-experts") },
        variant: "default",
    } : shouldPushNutritionistBooking && nutritionistConsultsLeft && nutritionistConsultsLeft > 0 ? {
        text: "👨‍⚕️️ Prenota consulto con nutrizionista",
        buttonText: "Prenota",
        onClick: () => { navigate("/equipe/your-experts") },
        variant: "default",
    }: nutriConsultsLeft.length > 0 ? {
        text: `${nutriConsultsLeft[0].expert.data.name} ${nutriConsultsLeft[0].expert.data.family_name}`,
        buttonText: "Connettiti",
        expertRole: nutriConsultsLeft[0].expert.role,
        consult: nutriConsultsLeft[0],
        onClick: () => { window.open(nutriConsultsLeft[0].connectUrl, "_blank") },
        variant: "default",
    }: {
        text: "📝 Rispondi al check-up",
        buttonText: "Rispondi",
        onClick: () => { navigate("/check-up-assessment-home-page") },
        variant: "default",
    }

    const config = configMap[bloodTracker as BloodTestTrackingStatus];

    return {
        ...config ?? defaultReturn,
        variant: config?.variant ?? "default",
    };
}